import clsx from 'clsx';
import { bool, func, number, object, oneOf, oneOfType, shape, string } from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldErrorMessage, Icon } from 'components';
import { useSafeState } from 'hooks';

import 'assets/styles/components/form/form-input.scss';

const FormInput = ({
  name,
  label,
  labelHidden = false,
  rules = {},
  inputRef = null,
  className = '',
  placeholder = '',
  type = 'text',
  length = '',
  required = false,
  disabled = false,
  readOnly = false,
  autoComplete = 'off',
  icon = null,
  helpMessage = '',
  dataTestId = null,
  min = 0,
  max = null,
  showError = true,
  /* use onChange and/or onBlur to intercept react-hook-form events */
  onChange = null,
  onBlur = null
}) => {
  const {
    register,
    formState: { errors }
  } = useFormContext();

  const { t } = useTranslation();

  const [showPassword, setShowPassword] = useSafeState(false);
  const isPassword = type === 'password' && (name === 'newPassword' || name === 'newPasswordConfirmation');
  const error = errors?.[name]?.message || errors?.[name]?.type;

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <label className={clsx('block', className, { invalid: error, disabled: disabled || readOnly })} aria-live="polite">
      <span className={clsx('label', labelHidden && 'sr-only')}>{label}</span>

      <div className={clsx('input-container', { 'has-icon': icon?.name || isPassword })}>
        {icon && <Icon name={icon.name} iconStyle={icon?.style ?? 'fas'} classes="input-icon" />}

        <input
          ref={inputRef}
          name={name}
          type={isPassword && showPassword ? 'text' : type}
          placeholder={placeholder}
          className="input placeholder:text-gray-400"
          required={required}
          readOnly={readOnly}
          disabled={disabled}
          autoComplete={autoComplete}
          data-test-id={dataTestId}
          min={min}
          max={max}
          {...(register &&
            register(name, {
              ...rules,
              onChange: (e) => onChange && onChange(e.target.value),
              onBlur: (e) => onBlur && onBlur(e.target.value)
            }))}
        />
      </div>
      {isPassword && (
        <button type="button" className="password-toggle-icon focus:text-yellow-500" onClick={togglePasswordVisibility}>
          <Icon name={showPassword ? 'eye-slash' : 'eye'} iconStyle="fas" classes="input-icon" />
          <span className="sr-only">{showPassword ? t('buttons.hidePassword') : t('buttons.showPassword')}</span>
        </button>
      )}
      {helpMessage && <p className="text-sm text-gray-600 mt-2">{helpMessage}</p>}

      {showError && <FieldErrorMessage {...{ errors, name, length, min, max }} />}
    </label>
  );
};

FormInput.propTypes = {
  label: string,
  name: string.isRequired,
  labelHidden: bool,
  inputRef: oneOfType([func, object]),
  rules: object,
  type: string,
  placeholder: string,
  className: string,
  length: string,
  required: bool,
  disabled: bool,
  readOnly: bool,
  autoComplete: string,
  icon: shape({ name: string.isRequired, style: oneOf(['fas', 'fad']) }),
  helpMessage: string,
  dataTestId: string,
  onChange: func,
  onBlur: func,
  min: number,
  max: number,
  showError: bool
};

export default FormInput;
